import { RadioGroup } from '@headlessui/react';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { ELEVEN_LABS_VOICES, ElevenLabsVoiceId, VoiceOption } from '../types/voice';
import { getVoiceSampleUrl } from '../utils/voiceUtils';

interface VoiceSelectorProps {
  selectedVoice: string;
  onVoiceChange: (voiceId: ElevenLabsVoiceId) => void;
  className?: string;
  /** Use 'wide' for fewer columns so cards are wider (e.g. on demo page where play buttons were cut off) */
  cardLayout?: 'default' | 'wide';
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  selectedVoice,
  onVoiceChange,
  className = '',
  cardLayout = 'default',
}) => {
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlaySample = (voice: VoiceOption) => {
    if (playingVoice === voice.id) {
      setPlayingVoice(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      return;
    }

    setPlayingVoice(voice.id);
    
    const sampleUrl = getVoiceSampleUrl(voice.id);

    // Real audio sample available
    if (audioRef.current) {
      audioRef.current.src = sampleUrl;
      audioRef.current.preservesPitch = true;
      audioRef.current.playbackRate = 1.0;
      audioRef.current.play().catch(() => {
        setPlayingVoice(null);
      });
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label htmlFor="agent-voice" className="block text-sm font-medium text-gray-700 mb-2">
        Agent Voice
      </label>
      
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        onEnded={() => setPlayingVoice(null)}
        onError={() => setPlayingVoice(null)}
        onLoadStart={() => {
          if (audioRef.current) {
            audioRef.current.preservesPitch = true;
            audioRef.current.playbackRate = 1.0;
          }
        }}
      />
      
      <RadioGroup value={selectedVoice} onChange={onVoiceChange}>
        <div className={`grid gap-4 ${
          cardLayout === 'wide'
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {ELEVEN_LABS_VOICES.map((voice) => (
            <RadioGroup.Option
              key={voice.id}
              value={voice.id}
              className={({ checked }) => 
                `relative w-full text-left border rounded-lg p-4 cursor-pointer transition-all ${
                  checked
                    ? 'border-primary bg-primary/5'
                    : 'border-input hover:border-primary/50'
                }`
              }
            >
              {({ checked }) => (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        checked ? 'border-primary bg-primary' : 'border-input'
                      }`}>
                        {checked && <div className="w-2 h-2 bg-white rounded-full"></div>}
                      </div>
                      <h3 className="font-medium text-foreground flex items-center gap-2">
                        {voice.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        voice.gender === 'Male' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-pink-100 text-pink-800'
                      }`}>
                        {voice.gender}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlaySample(voice);
                        }}
                        className={`p-2 rounded-full transition-colors ${
                          playingVoice === voice.id
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={playingVoice === voice.id ? 'Stop' : 'Play sample'}
                      >
                        {playingVoice === voice.id ? (
                          <PauseIcon className="h-4 w-4" />
                        ) : (
                          <PlayIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
              )}
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default VoiceSelector; 